import React from 'react';

export function Spinner() {
  return <div className="center-spin"><div className="spinner" /></div>;
}

export function Alert({ type = 'info', children }) {
  if (!children) return null;
  return <div className={`alert alert-${type}`} style={{marginTop:'16px'}}>{children}</div>;
}

export function Badge({ tone = 'neutral', children }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

const EyeIcon = ({ off }) => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
    <circle cx="12" cy="12" r="3" />
    {off && <line x1="3" y1="3" x2="21" y2="21" />}
  </svg>
);

export function PasswordInput(props) {
  const [visible, setVisible] = React.useState(false);
  return (
    <div className="password-field">
      <input {...props} type={visible ? 'text' : 'password'} />
      <button
        type="button"
        className="password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        title={visible ? 'Hide password' : 'Show password'}
        tabIndex={-1}
      >
        <EyeIcon off={visible} />
      </button>
    </div>
  );
}

export function Toggle({ checked, onChange, disabled }) {
  return (
    <label className="toggle">
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="toggle-slider" />
    </label>
  );
}

export function Modal({ title, onClose, children, wide }) {
  // Intentionally does NOT close on overlay/outside click - only the ✕ or the
  // modal's own Cancel button dismiss it, so a stray click can't lose form input.
  return (
    <div className="modal-overlay">
      <div className={`modal ${wide ? 'modal-wide' : ''}`}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function EmptyState({ title, sub, action }) {
  return (
    <div className="empty-state">
      <h3>{title}</h3>
      {sub && <p>{sub}</p>}
      {action}
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft: 'neutral', active: 'success', stopped: 'danger', completed: 'primary',
    in_progress: 'warning', pending_review: 'warning', evaluated: 'success',
    none: 'neutral', pending: 'warning', reviewed: 'success',
    admin: 'accent', user: 'primary',
    system: 'primary', ai: 'accent',
  };
  return <Badge tone={map[status] || 'neutral'}>{(status || '').replace(/_/g, ' ')}</Badge>;
}

/** Clickable table header that toggles sort on its column. */
export function SortHeader({ label, col, sort, onSort, className }) {
  const active = sort.key === col;
  return (
    <th
      className={`th-sort${active ? ' active' : ''}${className ? ' ' + className : ''}`}
      onClick={() => onSort(col)}
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="th-sort-inner">
        {label}
        <span className="sort-ind" aria-hidden="true">{active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}</span>
      </span>
    </th>
  );
}

/** Toggles sort direction on repeat clicks of the same column, else sorts the new column ascending. */
export function nextSort(sort, col) {
  return sort.key === col ? { key: col, dir: sort.dir === 'asc' ? 'desc' : 'asc' } : { key: col, dir: 'asc' };
}

/** Sorts a copy of `list` by `sort`, using the accessor map; no-op when no column is active. */
export function sortRows(list, sort, accessors) {
  const acc = sort.key && accessors[sort.key];
  if (!acc) return list;
  const factor = sort.dir === 'asc' ? 1 : -1;
  // Copy first so we never mutate the state array in place.
  return [...list].sort((a, b) => {
    const va = acc(a), vb = acc(b);
    if (va < vb) return -1 * factor;
    if (va > vb) return 1 * factor;
    return 0;
  });
}

export function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  return (
    <div className="pagination">
      <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>Previous</button>
      <span className="text-sm text-muted">Page {page} of {totalPages}</span>
      <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>Next</button>
    </div>
  );
}
